/**
 * The route guards, as three functions and one table.
 *
 * `.docs/08-web-foundation.md` §5 gives the rules and the tests give the table:
 * every (session kind × route) pair has a destination, and `guards.unit.test.ts`
 * enumerates all of them. They live here rather than inside the route files so
 * that enumeration is possible without a router.
 *
 * The rules:
 *
 *   - **nobody** → `/login`, carrying where they were trying to go. The whole
 *     `href` is kept, hash included, because `/app/billing#invoices` is a place and
 *     the fragment is half of it.
 *   - **a client user on `/studio`** → `/app`, with a notice. Not a 403 page: they
 *     did nothing wrong, they followed a link, and the portal they *do* have is one
 *     redirect away.
 *   - **staff on `/app`** → `/studio`. The one exception is an impersonation, and
 *     it needs no rule: a staff member viewing a client's portal has that client's
 *     session, so the principal is a `client_user` and this guard never sees them
 *     (`principal.ts` says why that is the shape).
 *   - **a restricted studio account on a manager's page** → `/studio/tasks`, with a
 *     notice (session 35). Both tiers belong inside the studio shell; the tier
 *     decides which pages of it, and `STUDIO_ROUTE_ROLE` below is that table.
 *
 * `beforeLoad` throws the answer as a `redirect`, so nothing renders first.
 */

import { redirect } from '@tanstack/react-router'

import type { StudioRole } from '@fareast/shared'

import type { Principal } from './principal.js'

/** Where the restricted tier lives, and the one studio page it is never refused. */
export const STUDIO_HOME = '/studio/tasks'

/**
 * Where each kind of person belongs when they have asked for nothing in particular.
 *
 * A restricted studio account lands on the board rather than on `/studio`, which is
 * Analytics and is a manager's. Sending them to a page they are refused is the trap
 * `packages/shared/src/schemas/clients.ts` records for the `billing` seat — "a role
 * locked out of its own landing page, and a rule that does that is wrong whichever
 * document implies it" — so it is not a judgement call.
 */
export function homeFor(principal: Principal): '/studio' | typeof STUDIO_HOME | '/app' | '/login' {
  if (principal.kind === 'staff_user') {
    return principal.role === 'manager' ? '/studio' : STUDIO_HOME
  }
  if (principal.kind === 'client_user') return '/app'
  return '/login'
}

/**
 * Which studio pages the restricted tier may open. **Default `manager`.**
 *
 * A table rather than a check inside each route file, and the default is the whole
 * point: a studio route added next year is closed to the restricted tier until
 * somebody writes a line here. The opposite arrangement — a `guardManagerRoute` each
 * manager-only file calls — would default a new route *open*, which is the failure
 * mode this session exists to remove.
 *
 * Keys are route paths and the match is longest-prefix, so `/studio/tasks/$ref`
 * inherits `/studio/tasks` without a second entry and `/studio` does not swallow the
 * rest of the tree. `pages/studio/rules.test.tsx` walks `routes/studio/` off disk and
 * fails on a file this table has no answer for.
 */
export const STUDIO_ROUTE_ROLE: Readonly<Record<string, StudioRole>> = {
  [STUDIO_HOME]: 'staff',
}

/**
 * The lowest tier that may open this studio path. `manager` unless the table says so.
 *
 * Longest-prefix, and the boundary has to be a `/` or the end of the string —
 * otherwise a future `/studio/tasks-archive` would inherit `/studio/tasks`'s answer
 * by sharing seven characters with it.
 */
export function studioRouteRole(pathname: string): StudioRole {
  let best: { readonly prefix: string; readonly role: StudioRole } | null = null

  for (const [prefix, role] of Object.entries(STUDIO_ROUTE_ROLE)) {
    const matches = pathname === prefix || pathname.startsWith(`${prefix}/`)
    if (!matches) continue
    if (best === null || prefix.length > best.prefix.length) best = { prefix, role }
  }

  return best?.role ?? 'manager'
}

/**
 * The destination for a principal at a route, or null to let them through.
 *
 * A pure function over the facts that decide it, which is what makes the table test a
 * table rather than a browser. Session 35 adds the **third** fact — which studio page
 * — rather than putting the tier check somewhere else, precisely so that property
 * survives: `pathname` is optional, and is only read for the studio portal.
 *
 * **A refused restricted account goes to `/studio/tasks`, never to `/app`.** Sending
 * them to the client portal would be an infinite redirect: this same function answers
 * `/studio` for any `staff_user` at `portal === 'client'`, and `/studio` is Analytics,
 * which is a manager's — `/studio/money` → `/app` → `/studio` → `/app` → … The board
 * is allowed for both tiers, so no loop is constructible through it.
 */
export function destinationFor(
  principal: Principal,
  portal: 'client' | 'studio',
  pathname?: string,
): 'allow' | '/login' | '/app' | '/studio' | typeof STUDIO_HOME {
  if (principal.kind === 'anonymous') return '/login'
  if (portal === 'client') return principal.kind === 'staff_user' ? '/studio' : 'allow'
  if (principal.kind === 'client_user') return '/app'

  // No path given means "the studio portal in general", which both tiers may enter.
  // The per-page answer is only asked when there is a page to ask about.
  if (pathname === undefined) return 'allow'
  if (principal.role === 'manager') return 'allow'
  return studioRouteRole(pathname) === 'manager' ? STUDIO_HOME : 'allow'
}

/** The one `?notice=` the guards raise. `/app` renders it and lets you dismiss it. */
export const STAFF_ONLY_NOTICE = 'staff-only'

/**
 * The studio's own, session 35's: a restricted account followed a link to a page that
 * is a manager's.
 *
 * A separate word from `STAFF_ONLY_NOTICE` because the two are read by different
 * people in different portals and say different things — one means "this portal is not
 * yours", the other "this page is not yours and the rest of the portal is".
 */
export const MANAGER_ONLY_NOTICE = 'manager-only'

/** `beforeLoad` for `/app`. Throws, or returns. */
export function guardClientPortal(principal: Principal, href: string): void {
  const destination = destinationFor(principal, 'client')
  if (destination === 'allow') return
  if (destination === '/login') throw redirect({ to: '/login', search: { redirect: href } })
  throw redirect({ to: destination })
}

/**
 * `beforeLoad` for `/studio`.
 *
 * `pathname` rather than `href`: the tier table is about the route, and a query string
 * or a fragment is not part of one. `href` is still what the sign-in redirect carries,
 * because that one is a *place* and the fragment is half of it.
 */
export function guardStudioPortal(principal: Principal, href: string, pathname: string): void {
  const destination = destinationFor(principal, 'studio', pathname)
  if (destination === 'allow') return
  if (destination === '/login') throw redirect({ to: '/login', search: { redirect: href } })
  if (destination === STUDIO_HOME) {
    throw redirect({ to: STUDIO_HOME, search: { notice: MANAGER_ONLY_NOTICE } })
  }
  throw redirect({ to: '/app', search: { notice: STAFF_ONLY_NOTICE } })
}
